import { computed, shallowRef, watch, type ComputedRef } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { getConversation, listConversations } from "./aiConversationsApi";
import type {
  AiConversationDetail,
  AiConversationPage,
  ConversationQuery,
} from "./aiConversationsTypes";

type DetailState =
  | { status: "idle" | "loading" | "error"; data: null }
  | { status: "ready"; data: AiConversationDetail };
interface ListState {
  status: "idle" | "loading" | "ready" | "error";
  data: AiConversationPage;
  loadingMore: boolean;
  moreFailed: boolean;
}
const emptyPage = (): AiConversationPage => ({
  items: [],
  total: 0,
  page: 0,
  page_size: 50,
  has_more: false,
});

/** Owns cancellable requests; only the current selection may publish results or errors. */
export function useAiConversationDetail(
  selectedId: ComputedRef<number | null>
) {
  const { globalErrorHandler } = useGlobalHandlers();
  const state = shallowRef<DetailState>({ status: "idle", data: null });
  const retry = shallowRef(0);
  watch(
    [selectedId, retry],
    async ([id], _, onCleanup) => {
      state.value = { status: id ? "loading" : "idle", data: null };
      if (!id) return;
      const controller = new AbortController();
      onCleanup(() => controller.abort());
      try {
        const data = await getConversation(id, controller.signal);
        if (!controller.signal.aborted) state.value = { status: "ready", data };
      } catch (error) {
        if (controller.signal.aborted) return;
        state.value = { status: "error", data: null };
        globalErrorHandler(error);
      }
    },
    { immediate: true, flush: "sync" }
  );
  return { state, retry: () => retry.value++ };
}

/** Paginates within one filter snapshot; failed pages can be retried without skipping rows. */
export function useAiConversationList(
  query: ComputedRef<ConversationQuery | null>
) {
  const { globalErrorHandler } = useGlobalHandlers();
  const state = shallowRef<ListState>({
    status: "idle",
    data: emptyPage(),
    loadingMore: false,
    moreFailed: false,
  });
  const key = computed(() => JSON.stringify(query.value));
  const retry = shallowRef(0);
  let loadNextPage: () => Promise<void> = async () => {};

  watch(
    [key, retry],
    (_, __, onCleanup) => {
      const params = query.value;
      state.value = {
        status: params ? "loading" : "idle",
        data: emptyPage(),
        loadingMore: false,
        moreFailed: false,
      };
      loadNextPage = async () => {};
      if (!params) return;
      const controller = new AbortController();
      onCleanup(() => controller.abort());
      async function load(append: boolean) {
        if (
          controller.signal.aborted ||
          (append &&
            (state.value.loadingMore || state.value.status !== "ready"))
        )
          return;
        state.value = {
          ...state.value,
          loadingMore: append,
          moreFailed: false,
        };
        try {
          const data = await listConversations(
            {
              ...params,
              page: state.value.data.page + 1,
              before_id: append ? state.value.data.items.at(-1)?.id : undefined,
              before_created_at: append
                ? state.value.data.items.at(-1)?.created_at
                : undefined,
            },
            controller.signal
          );
          if (controller.signal.aborted) return;
          state.value = {
            status: "ready",
            data: {
              ...data,
              items: append
                ? [...state.value.data.items, ...data.items]
                : data.items,
            },
            loadingMore: false,
            moreFailed: false,
          };
        } catch (error) {
          if (controller.signal.aborted) return;
          state.value = {
            ...state.value,
            status: append ? "ready" : "error",
            loadingMore: false,
            moreFailed: append,
          };
          globalErrorHandler(error);
        }
      }
      loadNextPage = () => load(true);
      void load(false);
    },
    { immediate: true, flush: "sync" }
  );

  return { state, loadMore: () => loadNextPage(), retry: () => retry.value++ };
}
